import { ReaderDocument, Theme } from '@narravero/web-reader-lib';
import { defaultColorsValues, defaultColorsValuesDark } from 'na-lib';
import { supportUrlStructure } from 'src/app/supportUrls';
import { environment } from 'src/environments/environment';

export interface ReaderDocumentContext {
  passId?: string;
  contextId?: string;
}

const MEDIA_URL = /^(pass|context|tridy):\/\//;

// Replaces pass://, context:// and tridy:// values (whole-string matches only, so text/markdown
// content is untouched) with the real backend URLs, anywhere in the given JSON structure.
function resolveMediaUrls(value: any, ctx: ReaderDocumentContext): any {
  if (typeof value === 'string') {
    if (!MEDIA_URL.test(value)) return value;
    const resolved = supportUrlStructure(value, ctx.passId, ctx.contextId);
    return resolved ? String(resolved) : value;
  }
  if (Array.isArray(value)) return value.map(v => resolveMediaUrls(v, ctx));
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const key of Object.keys(value)) out[key] = resolveMediaUrls(value[key], ctx);
    return out;
  }
  return value;
}

function normalizeFormWidgetsForReader(widgets: any[]): any[] {
  return widgets.map((widget: any) => {
    if (widget?.type !== 'form' || !Array.isArray(widget.sections)) return widget;
    return {
      ...widget,
      sections: widget.sections.map((section: any) => ({
        ...section,
        fields: Array.isArray(section?.fields)
          ? section.fields.map((field: any) => {
            if (field?.type !== 'boolean') return { ...field };
            return {
              ...field,
              showValueAsLabel: field.showValueAsLabel === true ? true : undefined,
              value: field.value == null ? undefined : String(field.value)
            };
          })
          : []
      }))
    };
  });
}

// Font urls come in more shapes than other media (see DppThemeService.supportUrl): besides
// pass:// and context:// they can be tridy://<passId>/<file> or a raw firebasestorage url.
function resolveFontUrl(url: any, ctx: ReaderDocumentContext): any {
  if (typeof url !== 'string') return url;
  const base = environment.useEmulators ? 'http://localhost:5000' : `https://${environment.urlHostName}`;
  const tridy = url.match(/^tridy:\/\/(.+)\/(.+)$/);
  if (tridy) return `${base}/v1/pass/${tridy[1]}/${tridy[2]}`;
  if (/^https:\/\/firebasestorage\.googleapis\.com/.test(url)) {
    try {
      return String(supportUrlStructure(url, ctx.passId, ctx.contextId) ?? url);
    } catch {
      return url;
    }
  }
  return resolveMediaUrls(url, ctx);
}

function resolveFonts(fonts: any, ctx: ReaderDocumentContext): any {
  if (!fonts) return fonts;
  // same condition as DppThemeService.updateFonts: a font needs both a name and a url,
  // otherwise it is not applied (and the reader falls back to the system font)
  const out: any = {};
  for (const key of Object.keys(fonts)) {
    const font = fonts[key];
    if (font?.name && font?.url) out[key] = { ...font, url: resolveFontUrl(font.url, ctx) };
  }
  return Object.keys(out).length ? out : undefined;
}

const placeholderUrl = (file: string) => new URL(`assets/img/${file}`, document.baseURI).href;

// Editor-only: widgets without media show the placeholders the editor always had.
function withPlaceholders(widgets: any[]): any[] {
  const image = placeholderUrl('image_placeholder.png');
  const video = placeholderUrl('video_placeholder.png');
  const missing = (o: any, ...keys: string[]) => !keys.some(k => !!o?.[k]);

  return widgets.map((w: any) => {
    switch (w?.type) {
      case 'image':
      case 'billboardImage':
        return missing(w, 'url', 'fallbackUrl') ? { ...w, url: image } : w;

      case 'callToAction':
        return missing(w, 'imageUrl', 'fallbackUrl') ? { ...w, imageUrl: image } : w;

      case 'text':
        return w.image && missing(w.image, 'url', 'fallbackUrl') ? { ...w, image: { ...w.image, url: image } } : w;

      case 'images':
      case 'imagesHorizontal':
      case 'imageSlider':
      case 'imageBlocks':
        return Array.isArray(w.images)
          ? { ...w, images: w.images.map((img: any) => (missing(img, 'url', 'fallbackUrl') ? { ...img, url: image } : img)) }
          : w;

      case 'video':
      case 'billboardVideo':
      case 'splashVideo':
        return missing(w, 'url', 'coverImageURL') ? { ...w, coverImageURL: video } : w;

      default:
        return w;
    }
  });
}

// Builds the document rendered by <wr-reader-shell> in preview mode from the editor state.
// Widget types unknown to the reader lib are passed through and render as "unsupported".
export function buildReaderDocument(
  widgets: any[],
  themeData: any,
  language?: string,
  ctx: ReaderDocumentContext = {},
): ReaderDocument {
  const theme: Theme = {
    lightColors: { ...defaultColorsValues, ...(themeData?.lightColors ?? {}) },
    darkColors: { ...defaultColorsValuesDark, ...(themeData?.darkColors ?? {}) },
    cornerRadius: themeData?.cornerRadius ?? 10,
    widgetSpacing: themeData?.widgetSpacing ?? 20,
    fonts: resolveFonts(themeData?.fonts, ctx),
  };

  return {
    version: '0.1',
    theme,
    widgets: withPlaceholders(resolveMediaUrls(normalizeFormWidgetsForReader((widgets ?? []).filter(w => !!w)), ctx)),
    language,
  };
}
